/**
 * Plugin operations — shared logic for CLI and UI actions.
 *
 * File-based: reads/writes nexus.plugins.json. No DB dependency.
 */

import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'node:fs'
import { join, dirname } from 'node:path'
import { exec } from 'node:child_process'
import { promisify } from 'node:util'
import type { PluginManifest } from '@gzl10/nexus-sdk'

const execAsync = promisify(exec)

const PLUGIN_PREFIX = '@gzl10/nexus-plugin-'

// ── Name helpers ──────────────────────────────────────────────────────────────

/** Normalize short name ("charts") to full npm name ("@gzl10/nexus-plugin-charts") */
export function normalizePluginName(name: string): string {
  if (name.startsWith('@gzl10/nexus-plugin-')) return name
  if (name.startsWith('@gzl10/nexus-')) return name.replace('@gzl10/nexus-', PLUGIN_PREFIX)
  if (name.startsWith('nexus-plugin-')) return `@gzl10/${name}`
  if (name.startsWith('nexus-')) return `${PLUGIN_PREFIX}${name.replace('nexus-', '')}`
  return `${PLUGIN_PREFIX}${name}`
}

/** Extract short name from full npm name */
export function shortPluginName(name: string): string {
  return name.replace(PLUGIN_PREFIX, '')
}

/**
 * Resolve plugin identifier to full npm name.
 * Accepts: 3-char code (e.g. 'cht'), short suffix ('charts'), or full name ('@gzl10/nexus-plugin-charts').
 * Falls back to normalizePluginName() for non-code identifiers or unknown codes.
 */
export function resolvePluginName(identifier: string, discovered: PluginManifest[]): string {
  if (/^[a-z]{3}$/.test(identifier)) {
    const found = discovered.find(p => p.code === identifier)
    if (found) return found.name
  }
  return normalizePluginName(identifier)
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PluginEntry {
  enabled: boolean
}

export type PluginsFile = Record<string, PluginEntry>

// ── File helpers ──────────────────────────────────────────────────────────────

/** Get path to nexus.plugins.json */
export function getPluginsFilePath(projectPath?: string): string {
  const base = projectPath || process.cwd()
  return join(base, 'data', 'nexus.plugins.json')
}

/** Read nexus.plugins.json (returns {} if missing) */
export function readPluginsFile(filePath?: string): PluginsFile {
  const path = filePath || getPluginsFilePath()
  try {
    return JSON.parse(readFileSync(path, 'utf-8'))
  } catch {
    return {}
  }
}

/** Write nexus.plugins.json */
export function writePluginsFile(data: PluginsFile, filePath?: string): void {
  const path = filePath || getPluginsFilePath()
  const dir = dirname(path)
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true })
  }
  writeFileSync(path, JSON.stringify(data, null, 2) + '\n', 'utf-8')
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** Get plugin state from nexus.plugins.json */
export function getPluginState(name: string, projectPath?: string): PluginEntry | undefined {
  const plugins = readPluginsFile(getPluginsFilePath(projectPath))
  return plugins[name]
}

/** Get all plugin states */
export function getAllPluginStates(projectPath?: string): PluginsFile {
  return readPluginsFile(getPluginsFilePath(projectPath))
}

// ── Operations ────────────────────────────────────────────────────────────────

/** Check if a package exists in the pnpm workspace */
async function isWorkspacePackage(name: string): Promise<boolean> {
  try {
    const { stdout } = await execAsync('pnpm list -r --json --depth -1')
    const packages = JSON.parse(stdout) as Array<{ name?: string }>
    return packages.some(p => p.name === name)
  } catch {
    return false
  }
}

/** Install plugin via pnpm and register in nexus.plugins.json */
export async function installPlugin(name: string, opts?: {
  version?: string
  projectPath?: string
}): Promise<void> {
  // Guard: prevent installing plugins inside the framework package itself
  const projectPath = opts?.projectPath ?? process.cwd()
  const pkgPath = join(projectPath, 'package.json')
  if (existsSync(pkgPath)) {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as { name?: string }
    if (pkg.name === '@gzl10/nexus-backend') {
      throw new Error('Cannot install plugins inside @gzl10/nexus-backend. Run this command from a Nexus project directory.')
    }
  }

  const isWorkspace = await isWorkspacePackage(name)

  let pkg: string
  if (opts?.version) {
    pkg = `${name}@${opts.version}`
  } else if (isWorkspace) {
    pkg = `${name}@workspace:*`
  } else {
    pkg = name
  }

  const flag = isWorkspace ? '-D ' : ''
  await execAsync(`pnpm add ${flag}${pkg}`)

  const filePath = getPluginsFilePath(opts?.projectPath)
  const plugins = readPluginsFile(filePath)
  plugins[name] = { enabled: true }
  writePluginsFile(plugins, filePath)
}

/** Check if a package is installed (listed in package.json dependencies or devDependencies) */
export function isPackageInstalled(name: string, projectPath?: string): boolean {
  const base = projectPath || process.cwd()
  const pkgPath = join(base, 'package.json')
  try {
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as {
      dependencies?: Record<string, string>
      devDependencies?: Record<string, string>
    }
    return !!(pkg.dependencies?.[name] || pkg.devDependencies?.[name])
  } catch {
    return false
  }
}

/** Uninstall plugin via pnpm and remove from nexus.plugins.json */
export async function uninstallPlugin(name: string, projectPath?: string): Promise<void> {
  const filePath = getPluginsFilePath(projectPath)
  const plugins = readPluginsFile(filePath)
  const isRegistered = name in plugins
  const isInstalled = isPackageInstalled(name, projectPath)

  if (!isRegistered && !isInstalled) {
    throw new Error(`Plugin ${shortPluginName(name)} is not installed`)
  }

  if (isInstalled) {
    await execAsync(`pnpm remove ${name}`)
  }

  if (isRegistered) {
    delete plugins[name]
    writePluginsFile(plugins, filePath)
  }
}

/** Enable a plugin */
export function enablePlugin(name: string, projectPath?: string): void {
  const filePath = getPluginsFilePath(projectPath)
  const plugins = readPluginsFile(filePath)

  if (!(name in plugins)) {
    throw new Error(`Plugin ${name} is not installed`)
  }

  plugins[name] = { enabled: true }
  writePluginsFile(plugins, filePath)
}

/** Disable a plugin */
export function disablePlugin(name: string, projectPath?: string): void {
  const filePath = getPluginsFilePath(projectPath)
  const plugins = readPluginsFile(filePath)

  if (!(name in plugins)) {
    throw new Error(`Plugin ${name} is not installed`)
  }

  plugins[name] = { enabled: false }
  writePluginsFile(plugins, filePath)
}
